<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'stock-withdrawals' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">{{ withdrawal?.reference || 'Détails du retrait' }}</h1>
          <p class="text-xs text-gray-400">Consultez les détails du retrait de stock</p>
        </div>
      </div>
      <div v-if="withdrawal && withdrawal.id" class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          :disabled="downloadingPdf"
          @click="downloadPdf">
          <span v-if="downloadingPdf" class="w-3 h-3 border-2 border-red-200 border-t-red-500 rounded-full animate-spin"></span>
          <i v-else class="fas fa-file-pdf text-[11px]"></i>
          PDF
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          :disabled="downloadingCsv"
          @click="downloadCsv">
          <span v-if="downloadingCsv" class="w-3 h-3 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin"></span>
          <i v-else class="fas fa-file-csv text-[11px]"></i>
          CSV
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
          :disabled="downloadingExcel"
          @click="downloadExcel">
          <span v-if="downloadingExcel" class="w-3 h-3 border-2 border-green-200 border-t-green-500 rounded-full animate-spin"></span>
          <i v-else class="fas fa-file-excel text-[11px]"></i>
          Excel
        </button>
        <button v-if="withdrawal.status === 'draft'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          @click="showCancelModal = true">
          <i class="fas fa-ban text-[11px]"></i>
          Annuler
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading"
      class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement des détails...</p>
    </div>

    <!-- Erreur / Non trouvé -->
    <div v-else-if="!withdrawal || !withdrawal.id"
      class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-circle-exclamation text-5xl"></i>
      </div>
      <h3 class="text-base font-semibold text-gray-700 mb-1">Retrait introuvable</h3>
      <p class="text-sm text-gray-400 max-w-sm mx-auto mb-6">Ce retrait n'existe pas ou a été supprimé.</p>
      <button
        class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'stock-withdrawals' })">
        <i class="fas fa-arrow-left text-xs"></i>
        Retour à la liste
      </button>
    </div>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte principale -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-file-alt text-primary text-sm"></i>
              </div>
              <div>
                <h2 class="text-base font-bold text-gray-900">{{ withdrawal.reference }}</h2>
                <p class="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                  <i class="fas fa-calendar text-[10px]"></i>
                  {{ formatDate(withdrawal.withdrawal_date) }}
                </p>
              </div>
            </div>
            <span :class="[
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold',
              getStatusBadgeClass(withdrawal.status)
            ]">
              <span class="w-1.5 h-1.5 rounded-full"
                :class="{
                  'bg-gray-400': withdrawal.status === 'draft',
                  'bg-emerald-500': withdrawal.status === 'completed',
                  'bg-red-500': withdrawal.status === 'cancelled'
                }"></span>
              {{ getStatusLabel(withdrawal.status) }}
            </span>
          </div>

          <div class="p-5">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <!-- Raison -->
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1.5">Raison du retrait</p>
                <span :class="[
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                  getReasonBadgeClass(withdrawal.reason)
                ]">
                  {{ getReasonLabel(withdrawal.reason) }}
                </span>
              </div>

              <!-- Créé par -->
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1.5">Créé par</p>
                <div class="flex items-center gap-2.5">
                  <div
                    class="w-8 h-8 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {{ getInitials(withdrawal.user?.name) }}
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900">{{ withdrawal.user?.name || '-' }}</p>
                    <p class="text-[11px] text-gray-400">{{ formatDateTime(withdrawal.created_at) }}</p>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div v-if="withdrawal.notes" class="md:col-span-2">
                <p class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1.5">Remarques</p>
                <div class="px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-600 leading-relaxed">
                  {{ withdrawal.notes }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tableau des articles -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Articles retirés ({{ withdrawal.items?.length || 0 }})</h3>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-50/80 border-b border-gray-200">
                  <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                    Qté
                  </th>
                  <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                    Avant
                  </th>
                  <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                    Après
                  </th>
                  <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                    Prix unit.
                  </th>
                  <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                    Total
                  </th>
                  <th v-if="hasNotes" class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="item in withdrawal.items || []" :key="item.id"
                  class="hover:bg-gray-50/80 transition-colors">
                  <!-- Produit -->
                  <td class="px-5 py-3.5">
                    <div class="flex items-center gap-2.5">
                      <div class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <i class="fas fa-box text-blue-500 text-[10px]"></i>
                      </div>
                      <div>
                        <p class="text-sm font-medium text-gray-900">
                          {{ item.product?.name || ('Produit #' + item.product_id) }}
                        </p>
                        <p v-if="item.product?.reference" class="text-[11px] text-gray-400 font-mono mt-0.5">
                          {{ item.product.reference }}
                        </p>
                      </div>
                    </div>
                  </td>

                  <!-- Quantité -->
                  <td class="px-5 py-3.5 text-center">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600">
                      -{{ item.quantity }}
                    </span>
                  </td>

                  <!-- Stock avant -->
                  <td class="px-5 py-3.5 text-center">
                    <span class="text-sm font-medium text-gray-700">{{ item.quantity_before ?? '-' }}</span>
                  </td>

                  <!-- Stock après -->
                  <td class="px-5 py-3.5 text-center">
                    <span class="text-sm font-medium text-gray-700">{{ item.quantity_after ?? '-' }}</span>
                  </td>

                  <!-- Prix unitaire -->
                  <td class="px-5 py-3.5 text-right">
                    <span class="text-sm text-gray-700">{{ formatCurrency(item.unit_price) }}</span>
                  </td>

                  <!-- Total -->
                  <td class="px-5 py-3.5 text-right">
                    <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(item.total_amount) }}</span>
                  </td>

                  <!-- Notes -->
                  <td v-if="hasNotes" class="px-5 py-3.5">
                    <span class="text-xs text-gray-500">{{ item.notes || '-' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b bg-primary/5 border-primary/10">
            <h3 class="text-sm font-semibold text-primary">Résumé</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-box text-gray-400 text-[10px]"></i>
                </span>
                Articles
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ withdrawal.items?.length || 0 }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-cubes text-blue-500 text-[10px]"></i>
                </span>
                Quantité totale
              </span>
              <span class="text-sm font-bold text-primary">{{ totalQuantity }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-coins text-emerald-500 text-[10px]"></i>
                </span>
                Valeur totale
              </span>
              <span class="text-sm font-bold text-emerald-600">{{ formatCurrency(totalValue) }}</span>
            </div>
          </div>
        </div>

        <!-- Chronologie -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Chronologie</h3>
          </div>
          <div class="p-5">
            <div class="relative">
              <!-- Ligne verticale -->
              <div v-if="withdrawal.updated_at && withdrawal.updated_at !== withdrawal.created_at"
                class="absolute left-[9px] top-5 bottom-5 w-px bg-gray-200"></div>

              <!-- Créé -->
              <div class="flex gap-3 mb-5">
                <div class="w-[18px] h-[18px] rounded-full bg-emerald-100 border-[3px] border-emerald-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">Retrait créé</p>
                  <p class="text-[11px] text-gray-400 mt-0.5">{{ formatDateTime(withdrawal.created_at) }}</p>
                  <p class="text-[11px] text-gray-400">{{ withdrawal.user?.name }}</p>
                </div>
              </div>

              <!-- Modifié -->
              <div v-if="withdrawal.updated_at && withdrawal.updated_at !== withdrawal.created_at" class="flex gap-3">
                <div class="w-[18px] h-[18px] rounded-full bg-amber-100 border-[3px] border-amber-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div class="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">Dernière modification</p>
                  <p class="text-[11px] text-gray-400 mt-0.5">{{ formatDateTime(withdrawal.updated_at) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
          <div class="space-y-1.5">
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="downloadPdf">
              <i class="fas fa-file-pdf text-xs w-4 text-center text-red-500"></i>
              Télécharger PDF
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="downloadCsv">
              <i class="fas fa-file-csv text-xs w-4 text-center text-blue-500"></i>
              Télécharger CSV
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="downloadExcel">
              <i class="fas fa-file-excel text-xs w-4 text-center text-green-500"></i>
              Télécharger Excel
            </button>
            <button v-if="withdrawal.status === 'draft'"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              @click="showCancelModal = true">
              <i class="fas fa-ban text-xs w-4 text-center"></i>
              Annuler ce retrait
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal d'annulation -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showCancelModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="showCancelModal = false">
          <div class="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div class="w-14 h-14 rounded-full bg-amber-50 mx-auto mb-4 flex items-center justify-center">
              <i class="fas fa-triangle-exclamation text-amber-500 text-xl"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-2">Annuler ce retrait ?</h3>
            <p class="text-sm text-gray-500 mb-6 leading-relaxed">
              Cette action marquera le retrait comme annulé. Êtes-vous sûr de vouloir continuer ?
            </p>
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="showCancelModal = false">
                Non, garder
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                @click="confirmCancel">
                Oui, annuler
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import StockWithdrawalService from '@/services/stockWithdrawalService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'StockWithdrawalDetailsView',
  setup() {
    const route = useRoute()
    const toastStore = useToastStore()

    const withdrawal = ref(null)
    const loading = ref(true)
    const showCancelModal = ref(false)
    const downloadingPdf = ref(false)
    const downloadingCsv = ref(false)
    const downloadingExcel = ref(false)

    const loadDetails = async () => {
      loading.value = true
      try {
        const id = route.params.id
        const res = await StockWithdrawalService.getStockWithdrawal(id)
        withdrawal.value = res.data?.data || res.data || {}
        withdrawal.value.items = withdrawal.value.items || []
      } catch (e) {
        console.error('Error loading withdrawal:', e)
        withdrawal.value = null
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement du retrait',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const totalQuantity = computed(() => {
      return StockWithdrawalService.calculateTotalQuantity(withdrawal.value?.items || [])
    })

    const totalValue = computed(() => {
      const items = withdrawal.value?.items || []
      return items.reduce((sum, item) => {
        return sum + (Number(item.total_amount) || 0)
      }, 0)
    })

    const hasNotes = computed(() => {
      const items = withdrawal.value?.items || []
      return items.some(item => item.notes && item.notes.trim())
    })

    const getReasonLabel = (reason) => {
      return StockWithdrawalService.getReasonLabel(reason)
    }

    const getStatusLabel = (status) => {
      return StockWithdrawalService.getStatusLabel(status)
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        'draft': 'bg-gray-100 text-gray-600',
        'completed': 'bg-emerald-50 text-emerald-700',
        'cancelled': 'bg-red-50 text-red-700'
      }
      return classes[status] || 'bg-gray-100 text-gray-600'
    }

    const getReasonBadgeClass = (reason) => {
      const classes = {
        'damage': 'bg-amber-50 text-amber-700',
        'loss': 'bg-amber-50 text-amber-700',
        'expired': 'bg-orange-50 text-orange-700',
        'theft': 'bg-red-50 text-red-700',
        'return_to_supplier': 'bg-blue-50 text-blue-700',
        'adjustment': 'bg-gray-100 text-gray-600',
        'other': 'bg-gray-100 text-gray-600'
      }
      return classes[reason] || 'bg-gray-100 text-gray-600'
    }

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '-'
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount) + ' FCFA'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return StockWithdrawalService.formatDate(dateString)
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const downloadPdf = async () => {
      downloadingPdf.value = true
      try {
        await StockWithdrawalService.downloadStockWithdrawalPdf(withdrawal.value.id)
        toastStore.addToast({
          type: 'success',
          message: 'PDF téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur lors du téléchargement PDF:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du téléchargement PDF',
          duration: 4000
        })
      } finally {
        downloadingPdf.value = false
      }
    }

    const downloadCsv = async () => {
      downloadingCsv.value = true
      try {
        await StockWithdrawalService.exportStockWithdrawal(withdrawal.value.id, { format: 'csv' })
        toastStore.addToast({
          type: 'success',
          message: 'Fichier CSV téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur lors du téléchargement CSV:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du téléchargement CSV',
          duration: 4000
        })
      } finally {
        downloadingCsv.value = false
      }
    }

    const downloadExcel = async () => {
      downloadingExcel.value = true
      try {
        await StockWithdrawalService.exportStockWithdrawal(withdrawal.value.id, { format: 'excel' })
        toastStore.addToast({
          type: 'success',
          message: 'Fichier Excel téléchargé avec succès',
          duration: 3000
        })
      } catch (error) {
        console.error('Erreur lors du téléchargement Excel:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du téléchargement Excel',
          duration: 4000
        })
      } finally {
        downloadingExcel.value = false
      }
    }

    const confirmCancel = async () => {
      try {
        await StockWithdrawalService.updateStockWithdrawal(withdrawal.value.id, {
          status: 'cancelled'
        })
        toastStore.addToast({
          type: 'success',
          message: 'Retrait annulé avec succès',
          duration: 3000
        })
        showCancelModal.value = false
        loadDetails()
      } catch (e) {
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'annulation',
          duration: 3000
        })
      }
    }

    onMounted(loadDetails)

    return {
      withdrawal,
      loading,
      totalQuantity,
      totalValue,
      hasNotes,
      showCancelModal,
      downloadingPdf,
      downloadingCsv,
      downloadingExcel,
      getReasonLabel,
      getStatusLabel,
      getStatusBadgeClass,
      getReasonBadgeClass,
      formatCurrency,
      formatDate,
      formatDateTime,
      getInitials,
      downloadPdf,
      downloadCsv,
      downloadExcel,
      confirmCancel
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}
</style>
